import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { nfRp } from '@/constants/nf';
import MealServingIcon from '@/components/Parent/MealServingIcon';
import { route } from '@/constants/routeName';
import {
	canOrderForChild,
	paidDishLinesFromServingTimes,
	plannerCardBadge,
	plannerSlotSummaries,
} from '@/helper/dashboardStatus';
import {
	EMPTY_CART_ITEMS,
	linesForChildAndDate,
	mealDateIso,
	unpaidTotalForLines,
} from '@/helper/cartFamily';
import useSwitchChild from '@/hooks/useSwitchChild';
import { pickMenuServingTimeForChild } from '@/helper/pickMenuServingTime';

/**
 * One child on the meal-planner Home (PA2-02).
 */
export default function ChildCard({ child, mealDate }) {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { switchChild, switchingLocked } = useSwitchChild();
	const cartItems = useSelector((state) => state.cart.items) ?? EMPTY_CART_ITEMS;
	const servingTimeSlots = useSelector((state) => state.content.servingTimes);
	const dateIso = mealDateIso(mealDate);

	const name = child.member?.name || `Child #${child.id}`;
	const studentId = child.member?.on_list_attributes?.student_id;
	const klass = child.member?.on_list_attributes?.class;
	const initials = name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((p) => p[0]?.toUpperCase())
		.join('');

	const unpaidLines = useMemo(
		() => linesForChildAndDate(cartItems, child.id, dateIso),
		[cartItems, child.id, dateIso]
	);

	const slots = useMemo(
		() => plannerSlotSummaries(child.serving_times, unpaidLines),
		[child.serving_times, unpaidLines]
	);

	const paidLines = useMemo(
		() => paidDishLinesFromServingTimes(child.serving_times),
		[child.serving_times]
	);

	const paidQtyTotal = slots.reduce((n, s) => n + s.paidQty, 0);
	const cartQtyTotal = unpaidLines.reduce((n, l) => n + (Number(l.quantity) || 0), 0);
	const badge = plannerCardBadge({ paidQtyTotal, cartQtyTotal, child });
	const allowOrder = canOrderForChild(child);
	const unpaidTotal = unpaidTotalForLines(unpaidLines);

	const onAddMoreMeals = () => {
		const result = switchChild(child.id);
		if (!result.ok) return;
		const nextId = pickMenuServingTimeForChild({
			childDashboardServingTimes: child.serving_times,
			cartItems,
			childId: child.id,
			mealDate: dateIso,
			servingTimeSlots,
		});
		if (nextId != null) {
			dispatch({ type: 'cart/setMenuServingTimeId', payload: nextId });
		}
		navigate(`/${route.menu}`);
	};

	const onRemoveLine = (uid) => {
		dispatch({ type: 'cart/removeItem', payload: uid });
	};

	const onClearMeals = () => {
		dispatch({ type: 'cart/clearChildItems', payload: child.id });
	};

	const badgeClass =
		badge.tone === 'paid' ? 'badge' : badge.tone === 'unpaid' ? 'badge draft' : 'badge empty';

	const slotClass = (slot) => {
		if (slot.paidQty > 0 && slot.cartQty === 0) return 'paid';
		if (slot.cartQty > 0) return 'draft';
		return 'muted';
	};

	return (
		<article className='kid'>
			<div className='kidhead'>
				<div className='avatar' aria-hidden='true'>
					{initials || '?'}
				</div>
				<div style={{ flex: 1, minWidth: 0 }}>
					<h2>{name}</h2>
					<p className='muted' style={{ fontSize: 12, margin: '2px 0 6px' }}>
						{klass || '—'}
						{studentId ? ` · Student ID ${studentId}` : ''}
					</p>
					<span className={badgeClass}>{badge.label}</span>
				</div>
			</div>

			<div className='status-grid'>
				{slots.map((slot) => (
					<div key={slot.name}>
						<strong>{slot.name}</strong>
						<span className={slotClass(slot)}>{slot.detail}</span>
					</div>
				))}
			</div>

			<div className='kidbody'>
				{paidLines.length > 0 && (
					<div>
						<p style={{ fontWeight: 600, fontSize: 13 }}>Confirmed meals</p>
						{paidLines.map((line) => (
							<div className='meal' key={line.key}>
								<MealServingIcon name={line.servingTimeName} />
								<div style={{ flex: 1 }}>
									<strong style={{ fontSize: 13 }}>{line.servingTimeName}</strong>
									<p className='muted' style={{ fontSize: 12, margin: '2px 0 0' }}>
										{line.quantity} × {line.name}
									</p>
								</div>
								<span className='price'>{nfRp(line.lineTotal)}</span>
							</div>
						))}
					</div>
				)}

				{unpaidLines.length > 0 && (
					<div className='draft-group'>
						<div className='row'>
							<strong style={{ fontSize: 12 }}>Saved selections · Not paid</strong>
							<button type='button' className='link' onClick={onClearMeals}>
								Clear meals
							</button>
						</div>
						{unpaidLines.map((line) => {
							const lineTotal =
								((('unit_price' in line
									? +line.unit_price
									: 'price' in line
										? +line.price
										: +line.selling_price) || 0) +
									(line.add_ons || []).reduce(
										(a, b) => a + (+b.add_on?.price || +b?.price || 0),
										0
									)) *
								(Number(line.quantity) || 0);
							const servingName = line.serving_time?.name || 'Meal';
							return (
								<div className='meal' key={line.uid}>
									<MealServingIcon name={servingName} />
									<div style={{ flex: 1 }}>
										<strong style={{ fontSize: 13 }}>{servingName}</strong>
										<p className='muted' style={{ fontSize: 12, margin: '2px 0 0' }}>
											{line.quantity} × {line.name || line.product_name || 'Item'}
										</p>
										<button type='button' className='link' onClick={() => onRemoveLine(line.uid)}>
											Remove
										</button>
									</div>
									<span className='price'>{nfRp(lineTotal)}</span>
								</div>
							);
						})}
						<p className='muted' style={{ fontSize: 12 }}>
							Meals are confirmed after payment.
						</p>
					</div>
				)}
			</div>

			<div className='kidfoot'>
				<div className='wallet-check'>
					<span className='muted'>Available balance</span>
					<strong>{nfRp(+child.balance || 0)}</strong>
					{unpaidTotal > 0 && (
						<>
							<span className='muted'>Unpaid for this date</span>
							<strong>{nfRp(unpaidTotal)}</strong>
						</>
					)}
				</div>
				{allowOrder && (
					<div className='kidactions'>
						<button type='button' disabled={switchingLocked} onClick={onAddMoreMeals}>
							Add more meals
						</button>
					</div>
				)}
			</div>
		</article>
	);
}
